import type {
  UserProfile,
  CollectionResponse,
  DecksResponse,
  StarterChooseResponse,
  PackBuyResponse,
  DeckSaveResponse,
  DeckWithId,
  GameReportResponse,
  GameReportRequest,
  StoreConfig,
  StoreBuyResponse,
  DailyGemsClaimResponse,
  BuyDeckSlotResponse,
  ApiError,
} from './shared/api-types.js';
import type { CardDef, DeckDef } from './types.js';
import type {
  TournamentSummary,
  TournamentPreview,
  TournamentDetail,
  LadderPage,
  StartMatchResponse,
  ReportTournamentMatchRequest,
  ReportTournamentMatchResponse,
  ClaimPrizeResponse,
} from './shared/tournament-types.js';
import type {
  AuctionDetail,
  AuctionMenuSummary,
  AuctionNotification,
  ListAuctionsResponse,
  PlaceBidResponse,
  ListAuctionRequest,
  ListAuctionResponse,
  ClaimAuctionResponse,
} from './shared/auction-types.js';

class ApiClientError extends Error {
  constructor(public status: number, message: string) {
    super(message);
    this.name = 'ApiClientError';
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const opts: RequestInit = {
    method,
    credentials: 'same-origin',
    headers: body ? { 'Content-Type': 'application/json' } : undefined,
    body: body ? JSON.stringify(body) : undefined,
  };

  const res = await fetch(path, opts);
  const data = await res.json();

  if (!res.ok) {
    throw new ApiClientError(res.status, (data as ApiError).error || 'Request failed');
  }

  return data as T;
}

export const apiClient = {
  // Auth
  async getSession(): Promise<UserProfile | null> {
    try {
      return await request<UserProfile>('GET', '/api/auth/me');
    } catch (e) {
      if (e instanceof ApiClientError && e.status === 401) return null;
      throw e;
    }
  },

  createGuest(): Promise<UserProfile> {
    return request<UserProfile>('POST', '/api/auth/guest');
  },

  register(username: string, password: string): Promise<UserProfile> {
    return request<UserProfile>('POST', '/api/auth/register', { username, password });
  },

  login(username: string, password: string): Promise<UserProfile> {
    return request<UserProfile>('POST', '/api/auth/login', { username, password });
  },

  logout(): Promise<{ ok: true }> {
    return request<{ ok: true }>('POST', '/api/auth/logout');
  },

  // Settings
  setProfileCard(cardId: string): Promise<UserProfile> {
    return request<UserProfile>('POST', '/api/auth/me/profile-card', { cardId });
  },

  setUsername(username: string): Promise<UserProfile> {
    return request<UserProfile>('POST', '/api/auth/me/username', { username });
  },

  setPassword(currentPassword: string, newPassword: string): Promise<{ ok: true }> {
    return request<{ ok: true }>('POST', '/api/auth/me/password', { currentPassword, newPassword });
  },

  // Collection
  getCollection(): Promise<CollectionResponse> {
    return request<CollectionResponse>('GET', '/api/collection');
  },

  chooseStarter(deckName: string, cards: { id: string; count: number }[]): Promise<StarterChooseResponse> {
    return request<StarterChooseResponse>('POST', '/api/starter/choose', { deckName, cards });
  },

  buyPack(packId: string, useTicket?: string): Promise<PackBuyResponse> {
    return request<PackBuyResponse>('POST', '/api/pack/buy', { packId, useTicket });
  },

  // Store (gems, coins, subscriptions). Path stays /api/gem-shop/* —
  // the gem-shop endpoint pre-dates the coin offers and there are no
  // external clients to migrate.
  getStoreConfig(): Promise<StoreConfig> {
    return request<StoreConfig>('GET', '/api/gem-shop/offers');
  },

  buyStoreOffer(offerId: string): Promise<StoreBuyResponse> {
    return request<StoreBuyResponse>('POST', '/api/gem-shop/buy', { offerId });
  },

  claimDailyGems(): Promise<DailyGemsClaimResponse> {
    return request<DailyGemsClaimResponse>('POST', '/api/gems/claim-daily');
  },

  // Decks
  getDecks(): Promise<DecksResponse> {
    return request<DecksResponse>('GET', '/api/decks');
  },

  saveDeck(deckId: string | undefined, name: string, cards: { id: string; count: number }[]): Promise<DeckSaveResponse> {
    return request<DeckSaveResponse>('POST', '/api/decks/save', { deckId, name, cards });
  },

  deleteDeck(deckId: string): Promise<{ ok: true }> {
    return request<{ ok: true }>('POST', '/api/decks/delete', { deckId });
  },

  setActiveDeck(deckId: string): Promise<{ ok: true }> {
    return request<{ ok: true }>('POST', '/api/decks/set-active', { deckId });
  },

  buyDeckSlot(): Promise<BuyDeckSlotResponse> {
    return request<BuyDeckSlotResponse>('POST', '/api/decks/buy-slot');
  },

  // Game
  reportGame(req: GameReportRequest): Promise<GameReportResponse> {
    return request<GameReportResponse>('POST', '/api/game/report', req);
  },

  // Rules
  getRulesBundle(): Promise<import('./shared/rules.js').RulesBundle> {
    return request<import('./shared/rules.js').RulesBundle>('GET', '/api/rules/bundle');
  },

  // Custom packs
  getMyCustomCards(): Promise<{ cards: CardDef[] }> {
    return request<{ cards: CardDef[] }>('GET', '/api/custom-cards/my');
  },

  /** Batch-fetch CardDefs for custom card IDs the caller doesn't own —
   *  e.g. another player's tournament-profile card. The server filters
   *  the input to known custom cards (premade IDs are silently dropped
   *  since they're already in the local catalog) and caps the input
   *  size at 200. */
  getCustomCardsByIds(ids: string[]): Promise<{ cards: CardDef[] }> {
    return request<{ cards: CardDef[] }>('POST', '/api/custom-cards/by-ids', { ids });
  },

  /** Public dossier subset (voice lines + can_speak) for one card.
   *  Returns null on 404 (missing dossier is a normal case — most
   *  custom cards won't have one yet). Other errors propagate. */
  async getDossierPublic(cardId: string): Promise<import('./shared/dossier-types.js').DossierPublic | null> {
    try {
      return await request<import('./shared/dossier-types.js').DossierPublic>('GET', `/api/dossiers/${encodeURIComponent(cardId)}`);
    } catch (e) {
      if (e instanceof ApiClientError && e.status === 404) return null;
      throw e;
    }
  },

  /** Cursor query for "which dossiers became ready since `ts`?". Pass
   *  null on the first call to bootstrap the cursor without listing
   *  anything; reuse the returned `ts` on subsequent calls. */
  getDossiersUpdatedSince(ts: string | null): Promise<{ ts: string; cardIds: string[] }> {
    const path = ts
      ? `/api/dossiers/updated-since?ts=${encodeURIComponent(ts)}`
      : `/api/dossiers/updated-since`;
    return request('GET', path);
  },

  buyCustomPack(hint?: string, useTicket?: string): Promise<{ jobId: string; gems: number }> {
    const body: { hint?: string; useTicket?: string } = {};
    if (hint && hint.trim()) body.hint = hint.trim();
    if (useTicket) body.useTicket = useTicket;
    return request<{ jobId: string; gems: number }>('POST', '/api/pack/custom/buy', Object.keys(body).length ? body : undefined);
  },

  keepCustomCards(jobId: string, keepIds: string[], imageChoices: Record<string, number>): Promise<{ gems: number; keptCardIds: string[]; cost: number }> {
    return request<{ gems: number; keptCardIds: string[]; cost: number }>('POST', `/api/pack/custom/${jobId}/keep`, { keepIds, imageChoices });
  },

  getLatestActiveCustomPack(): Promise<{ job: { id: string; status: string } | null }> {
    return request<{ job: { id: string; status: string } | null }>('GET', '/api/pack/custom/latest-active');
  },

  // Tournaments
  getTournaments(): Promise<{ tournaments: TournamentSummary[]; previews: TournamentPreview[] }> {
    return request<{ tournaments: TournamentSummary[]; previews: TournamentPreview[] }>('GET', '/api/tournaments');
  },

  getTournamentDetail(id: string): Promise<TournamentDetail> {
    return request<TournamentDetail>('GET', `/api/tournaments/${id}`);
  },

  getTournamentLadder(id: string, offset: number, limit: number): Promise<LadderPage> {
    return request<LadderPage>('GET', `/api/tournaments/${id}/ladder?offset=${offset}&limit=${limit}`);
  },

  startTournamentMatch(id: string): Promise<StartMatchResponse> {
    return request<StartMatchResponse>('POST', `/api/tournaments/${id}/start-match`);
  },

  reportTournamentMatch(
    id: string,
    matchId: string,
    body: ReportTournamentMatchRequest,
  ): Promise<ReportTournamentMatchResponse> {
    return request<ReportTournamentMatchResponse>(
      'POST',
      `/api/tournaments/${id}/matches/${matchId}/report`,
      body,
    );
  },

  claimTournamentPrize(id: string): Promise<ClaimPrizeResponse> {
    return request<ClaimPrizeResponse>('POST', `/api/tournaments/${id}/claim-prize`);
  },

  // ── Auctions ────────────────────────────────────────────────────

  listAuctions(): Promise<ListAuctionsResponse> {
    return request<ListAuctionsResponse>('GET', '/api/auctions');
  },

  getAuctionDetail(id: string): Promise<AuctionDetail> {
    return request<AuctionDetail>('GET', `/api/auctions/${encodeURIComponent(id)}`);
  },

  placeAuctionBid(id: string, maxBid: number): Promise<PlaceBidResponse> {
    return request<PlaceBidResponse>('POST', `/api/auctions/${encodeURIComponent(id)}/bid`, { maxBid });
  },

  listAuctionCard(req: ListAuctionRequest): Promise<ListAuctionResponse> {
    return request<ListAuctionResponse>('POST', '/api/auctions/list', req);
  },

  claimAuction(id: string, variant: number): Promise<ClaimAuctionResponse> {
    return request<ClaimAuctionResponse>('POST', `/api/auctions/${encodeURIComponent(id)}/claim`, { variant });
  },

  getAuctionSummary(): Promise<AuctionMenuSummary> {
    return request<AuctionMenuSummary>('GET', '/api/auctions/summary');
  },

  getAuctionNotifications(): Promise<{ notifications: AuctionNotification[] }> {
    return request<{ notifications: AuctionNotification[] }>('GET', '/api/auctions/notifications');
  },

  markAuctionNotificationsRead(): Promise<{ marked: number }> {
    return request<{ marked: number }>('POST', '/api/auctions/notifications/read');
  },
};
